<template>
  <div class="bg-white rounded-lg shadow-md border border-gray-200">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-800">Bandeja de Mensajes</h3>
        <div class="flex items-center space-x-2">
          <div v-if="unreadCount > 0" class="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
            {{ unreadCount }} nuevos
          </div>
          <button
            @click="refreshMessages"
            class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            :disabled="loading"
          >
            <svg class="h-5 w-5" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span class="ml-3 text-gray-600">Cargando mensajes...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="px-6 py-4">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error al cargar mensajes</h3>
            <p class="text-sm text-red-700 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Messages List -->
    <div v-else-if="messages.length > 0" class="divide-y divide-gray-200 max-h-96 overflow-y-auto">
      <div
        v-for="message in messages"
        :key="message.id"
        class="px-6 py-4 hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
        :class="{ 'bg-blue-50 border-l-4 border-l-blue-500': !message.is_read }"
        @click="markAsRead(message)"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <!-- Message Header -->
            <div class="flex items-center space-x-2 mb-2">
              <h4 class="text-sm font-medium text-gray-900">{{ message.subject || 'Sin asunto' }}</h4>
              <span
                v-if="!message.is_read"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                Nuevo
              </span>
            </div>

            <!-- Message Content -->
            <p class="text-sm text-gray-600 mb-2 line-clamp-2">
              {{ message.content || 'Sin contenido' }}
            </p>

            <!-- Message Meta -->
            <div class="flex items-center justify-between text-xs text-gray-500">
              <div class="flex items-center space-x-4">
                <span class="flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  {{ message.sender_name || 'Sistema' }}
                </span>
                <span class="flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {{ formatDateTime(message.created_at) }}
                </span>
              </div>
              <div v-if="message.message_type" class="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {{ getMessageTypeText(message.message_type) }}
              </div>
            </div>
          </div>

          <!-- Message Actions -->
          <div class="ml-4 flex flex-col space-y-1">
            <button
              @click.stop="viewMessage(message)"
              class="text-blue-600 hover:text-blue-800 text-xs font-medium"
            >
              Ver
            </button>
            <button
              v-if="!message.is_read"
              @click.stop="markAsRead(message)"
              class="text-green-600 hover:text-green-800 text-xs font-medium"
            >
              Marcar leído
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="px-6 py-8 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No tienes mensajes</h3>
      <p class="mt-1 text-sm text-gray-500">Cuando recibas mensajes, aparecerán aquí.</p>
    </div>
  </div>

  <!-- Message Modal -->
  <div v-if="showModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Loading -->
      <div v-if="loadingMessage" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Cargando mensaje...</span>
      </div>

      <!-- Message Content -->
      <div v-else-if="selectedMessage" class="p-6">
        <!-- Header -->
        <div class="flex justify-between items-start mb-4">
          <div>
            <h2 class="text-xl font-semibold text-gray-800">{{ selectedMessage.subject }}</h2>
            <p class="text-sm text-gray-600 mt-1">De: {{ selectedMessage.sender_name }}</p>
            <p class="text-sm text-gray-500">{{ formatDateTime(selectedMessage.created_at) }}</p>
          </div>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="mb-6">
          <div v-if="selectedMessage.is_html" v-html="selectedMessage.body" class="text-gray-700"></div>
          <div v-else class="text-gray-700 whitespace-pre-wrap">{{ selectedMessage.body }}</div>
        </div>

        <!-- Attachments -->
        <div v-if="selectedMessage.attachments && selectedMessage.attachments.length > 0" class="mb-6">
          <h3 class="text-lg font-medium text-gray-800 mb-2">Adjuntos</h3>
          <div class="space-y-2">
            <div v-for="attachment in selectedMessage.attachments" :key="attachment.id" class="flex items-center space-x-2">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
              </svg>
              <a :href="attachment.file_path" target="_blank" class="text-blue-600 hover:text-blue-800">{{ attachment.file_name }}</a>
            </div>
          </div>
        </div>

        <!-- Recipients -->
        <div v-if="selectedMessage.recipients && selectedMessage.recipients.length > 0" class="mb-6">
          <h3 class="text-lg font-medium text-gray-800 mb-2">Destinatarios ({{ selectedMessage.recipients_count }})</h3>
          <div class="flex flex-wrap gap-2">
            <span v-for="recipient in selectedMessage.recipients" :key="recipient.id" class="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
              {{ recipient.user_name }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end">
          <button @click="closeModal" class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MyMessages',
  data() {
    return {
      messages: [],
      loading: true,
      error: null,
      unreadCount: 0,
      showModal: false,
      selectedMessage: null,
      loadingMessage: false,
    };
  },
  mounted() {
    this.getMessages();
  },
  methods: {
    getMessages() {
      this.loading = true;
      this.error = null;

      this.$http.get('/my-messages')
        .then(response => {
          const data = response.data;
          this.messages = data.messages || data.data || [];
          this.unreadCount = this.messages.filter(msg => !msg.is_read).length;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching messages:', error);
          this.error = error.message || 'Error al cargar los mensajes';
          this.messages = [];
          this.unreadCount = 0;
          this.loading = false;
        });
    },
    refreshMessages() {
      this.getMessages();
    },
    markAsRead(message) {
      if (message.is_read) return;

      // Optimistic update
      message.is_read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);

      // API call to mark as read (if endpoint exists)
      if (message.id) {
        this.$http.put(`/messages/${message.id}/read`)
          .catch(error => {
            console.error('Error marking message as read:', error);
            // Revert optimistic update
            message.is_read = false;
            this.unreadCount += 1;
          });
      }
    },
    async viewMessage(message) {
      this.loadingMessage = true;
      this.showModal = true;

      try {
        const response = await this.$http.get(`/messages/${message.id}`);
        this.selectedMessage = response.data;
      } catch (error) {
        console.error('Error fetching message:', error);
        this.error = 'Error al cargar el mensaje';
        this.showModal = false;
      } finally {
        this.loadingMessage = false;
      }

      // Mark as read when viewing
      if (!message.is_read) {
        this.markAsRead(message);
      }
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'Fecha no disponible';

      const date = new Date(dateTimeString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return date.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } else if (diffInHours < 168) { // Less than a week
        return date.toLocaleDateString('es-ES', {
          weekday: 'short',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        return date.toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'short',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
    },
    getMessageTypeText(messageType) {
      const typeMap = {
        notification: 'Notificación',
        announcement: 'Anuncio',
        reminder: 'Recordatorio',
        alert: 'Alerta',
        info: 'Información'
      };
      return typeMap[messageType] || messageType;
    },
    closeModal() {
      this.showModal = false;
      this.selectedMessage = null;
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
